import { useQuery, useMutation } from '@tanstack/react-query'
import { api } from '@/lib/api'

// Types for Places API
export interface PlacePrediction {
  place_id: string
  description: string
  structured_formatting?: {
    main_text: string
    secondary_text: string
  }
}

export interface PlaceDetails {
  name: string
  formatted_address?: string
  place_id: string
  photoUrl?: string
  city?: string
  address?: string
  website?: string
}

// Query key factory
export const placesKeys = {
  all: ['places'] as const,
  autocomplete: (input: string) => [...placesKeys.all, 'autocomplete', input] as const,
  details: (placeId: string) => [...placesKeys.all, 'details', placeId] as const,
  openingHours: (placeId: string, timezone?: string) =>
    [...placesKeys.all, 'openingHours', placeId, timezone] as const,
}

/**
 * Hook to search for places using Google Places Autocomplete
 */
export function usePlaceAutocomplete(input: string, enabled = true) {
  return useQuery({
    queryKey: placesKeys.autocomplete(input),
    queryFn: async (): Promise<PlacePrediction[]> => {
      if (!input || input.length < 3) return []

      const response = await api.get(`/places/autocomplete`, {
        params: {
          input,
          types: 'establishment',
        },
      })

      return response.data.predictions || []
    },
    enabled: enabled && !!input && input.length >= 3,
    staleTime: 60 * 1000, // Cache for 1 minute
  })
}

/**
 * Hook to get place details by place ID
 */
export function usePlaceDetails(placeId: string | undefined | null) {
  return useQuery({
    queryKey: placesKeys.details(placeId || ''),
    queryFn: async (): Promise<PlaceDetails> => {
      const response = await api.get(`/places/details/${placeId}`)
      return response.data
    },
    enabled: !!placeId,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  })
}

/**
 * Hook to get opening hours for a place
 */
export function useOpeningHours(
  placeId: string | undefined | null,
  timezone: string = 'Europe/Amsterdam',
  enabled = true
) {
  return useQuery({
    queryKey: placesKeys.openingHours(placeId || '', timezone),
    queryFn: async (): Promise<string> => {
      const response = await api.get(`/places/opening-hours`, {
        params: {
          place_id: placeId,
          timezone,
        },
      })
      return response.data.opening_hours
    },
    enabled: enabled && !!placeId,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  })
}

/**
 * Hook to fetch timezones list
 */
export function useTimezones(enabled = true) {
  return useQuery({
    queryKey: ['timezones'],
    queryFn: async (): Promise<string[]> => {
      const response = await api.get('/assistants/timezones')
      return response.data
    },
    enabled,
    staleTime: 24 * 60 * 60 * 1000, // Cache for 24 hours (timezones rarely change)
  })
}
